import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm, useWatch, type Control, type UseFormRegister } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  reportTemplateBodySchema,
  templateMaxPoints,
  type ReportField,
  type ReportItem,
  type ReportTemplateBody,
  type ReportTemplateDto,
} from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { SelectField, TextField } from '../components/ui/Field';
import { SkeletonCard } from '../components/ui/Skeleton';
import { KIND_LABEL, MODE_LABEL } from '../components/teacher-reports/format';

export const Route = createFileRoute('/_authed/system/teacher-report-templates/$templateId')({
  component: TemplateEditorPage,
});

const TYPE_LABEL: Record<ReportItem['type'], string> = {
  check: 'Tick box (teacher confirms)',
  auto: 'Checked by the portal',
  share: 'Share on WhatsApp',
  upload: 'Attach a file',
  counter: 'Number with a target',
};
const VERIFY_LABEL: Record<NonNullable<ReportItem['verify']>, string> = {
  attendance_marked: 'Attendance marked for the class',
  exam_marks_entered: 'Month exam marks entered',
};
const SHOW_WHEN_LABEL: Record<NonNullable<ReportItem['showWhen']>, string> = {
  someone_absent: 'Only when someone was absent',
  fees_pending: 'Only when fees are pending',
};

const slugify = (label: string, taken: Set<string>): string => {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/gu, '_')
      .replace(/^_+|_+$/gu, '')
      .replace(/^(\d)/u, 'item_$1')
      .slice(0, 40) || 'item';
  let key = base.length >= 2 ? base : `${base}_item`;
  for (let i = 2; taken.has(key); i++) key = `${base}_${i}`;
  return key;
};

const TOP_PART: Record<string, string> = {
  title: 'Title',
  basePoints: 'On-time points',
  groupsPoints: 'Student-group points',
  reflectPoints: 'Reflection points',
};
const SUB_PART: Record<string, string> = {
  points: 'points',
  target: 'target',
  verify: 'what the portal checks',
  hint: 'hint',
  labelMl: 'in Malayalam',
  hintMl: 'hint in Malayalam',
  placeholder: 'example answer',
  placeholderMl: 'example answer in Malayalam',
};

interface CheckIssue {
  path: ReadonlyArray<string | number>;
  code: string;
  message: string;
  minimum?: unknown;
  maximum?: unknown;
  type?: unknown;
}

/** A validation problem as a plain sentence, e.g. "Item 3: write at least 3 letters." */
function templateProblem(issue: CheckIssue | undefined): string {
  if (!issue) return 'Please check the checklist.';
  const [head, index, sub] = issue.path;
  const where =
    head === 'items' || head === 'fields'
      ? typeof index === 'number'
        ? `${head === 'items' ? 'Item' : 'Question'} ${index + 1}${typeof sub === 'string' && SUB_PART[sub] ? `, ${SUB_PART[sub]}` : ''}`
        : head === 'items'
          ? 'Checklist items'
          : 'Written questions'
      : head === 'groups'
        ? index === 'topMin'
          ? 'Top group from (%)'
          : 'Medium group from (%)'
        : (TOP_PART[String(head)] ?? 'The checklist');
  const n = (v: unknown) => (typeof v === 'number' ? v : Number(v));
  let what: string;
  if (issue.code === 'too_small') {
    what =
      issue.type === 'string'
        ? `write at least ${n(issue.minimum)} letters`
        : issue.type === 'array'
          ? 'add at least one item'
          : `use ${n(issue.minimum)} or more`;
  } else if (issue.code === 'too_big') {
    what =
      issue.type === 'string'
        ? `use no more than ${n(issue.maximum)} letters`
        : issue.type === 'array'
          ? `keep it to ${n(issue.maximum)} or fewer`
          : `use ${n(issue.maximum)} or less`;
  } else if (issue.code === 'invalid_type' || issue.code === 'not_integer') {
    what = 'enter a whole number';
  } else if (sub === 'target') {
    what = 'set a target';
  } else if (sub === 'verify') {
    what = 'choose what the portal checks';
  } else if (head === 'groups') {
    what = 'the Medium group must start below the Top group';
  } else if (issue.path.length === 1 && (head === 'items' || head === 'fields')) {
    what = 'two of them came out the same — change the wording of one';
  } else {
    what = 'please check this';
  }
  return `${where}: ${what}.`;
}

function TemplateEditorPage() {
  const { templateId } = Route.useParams();
  const canView = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin'));
  const isSuperAdmin = useAuthStore((s) => s.user?.primaryRole === 'super_admin');

  const query = useQuery({
    queryKey: ['teacher-reports', 'template', templateId] as const,
    enabled: canView,
    queryFn: ({ signal }) =>
      apiRequest<ReportTemplateDto>(`/api/teacher-reports/templates/${encodeURIComponent(templateId)}`, { signal }),
  });

  const back = (
    <Link to="/system/teacher-reports" className="inline-flex items-center gap-1 text-sm text-gold-700 hover:underline dark:text-gold-300">
      <ArrowLeft size={14} aria-hidden="true" /> Report settings
    </Link>
  );

  if (query.isLoading) {
    return (
      <ListShell title="Checklist">
        <SkeletonCard />
      </ListShell>
    );
  }
  if (query.isError || !query.data) {
    return (
      <ListShell title="Checklist">
        {back}
        <ErrorState
          title="Could not load this checklist"
          description={query.error instanceof ApiError ? query.error.message : undefined}
          onRetry={() => void query.refetch()}
        />
      </ListShell>
    );
  }

  const t = query.data;
  const title = `${KIND_LABEL[t.kind]} · ${MODE_LABEL[t.mode]}`;
  const editable = isSuperAdmin && t.status === 'draft';
  return (
    <ListShell
      title={title}
      description={`Version ${t.version} · ${t.status === 'draft' ? 'draft — not live yet' : t.status === 'active' ? 'live' : 'retired'}`}
    >
      {back}
      {editable ? <TemplateEditor template={t} /> : <TemplateReadOnly template={t} />}
    </ListShell>
  );
}

function TemplateReadOnly({ template: t }: { template: ReportTemplateDto }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[color:var(--color-text-muted)]">
        {t.basePoints} points on time · {t.groupsPoints} points for student groups
        {t.reflectPoints ? ` · ${t.reflectPoints} points for reflections` : ''} · up to <b>{t.maxPoints}</b> points in all.
      </p>
      <ol className="divide-y divide-dashed divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
        {t.items.map((item) => (
          <li key={item.key} className="flex items-start justify-between gap-3 px-4 py-2.5">
            <div className="min-w-0">
              <p className="font-medium">{item.label}</p>
              {item.labelMl ? (
                <p lang="ml" className="text-sm text-[color:var(--color-text-muted)]">
                  {item.labelMl}
                </p>
              ) : null}
              <p className="text-xs text-[color:var(--color-text-muted)]">
                {TYPE_LABEL[item.type]}
                {item.verify ? ` · ${VERIFY_LABEL[item.verify]}` : ''}
                {item.target ? ` · target ${item.target}` : ''}
                {item.showWhen ? ` · ${SHOW_WHEN_LABEL[item.showWhen]}` : ''}
              </p>
            </div>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-gold-700 dark:text-gold-300">+{item.points}</span>
          </li>
        ))}
      </ol>
      {t.fields.length > 0 ? (
        <div>
          <h3 className="mb-1 text-sm font-semibold">Written answers</h3>
          <ul className="list-inside list-disc text-sm text-[color:var(--color-text-muted)]">
            {t.fields.map((f) => (
              <li key={f.key}>
                {f.label}
                {f.labelMl ? <span lang="ml"> · {f.labelMl}</span> : null}
                {f.reflect ? ' (counts toward reflection points)' : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

type FormValues = ReportTemplateBody;

function TemplateEditor({ template }: { template: ReportTemplateDto }) {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const originalKeys = useMemo(() => new Set(template.items.map((i) => i.key)), [template]);

  const defaults: FormValues = useMemo(
    () => ({
      title: template.title,
      basePoints: template.basePoints,
      groupsPoints: template.groupsPoints,
      reflectPoints: template.reflectPoints,
      items: template.items,
      fields: template.fields,
      groups: template.groups,
      voiceNote: template.voiceNote,
    }),
    [template],
  );
  const { register, control, handleSubmit, reset, getValues, formState } = useForm<FormValues>({ defaultValues: defaults });
  useEffect(() => reset(defaults), [defaults, reset]);
  const items = useFieldArray({ control, name: 'items' });
  const watched = useWatch({ control }) as FormValues;
  const maxPoints = templateMaxPoints({
    basePoints: Number(watched.basePoints) || 0,
    groupsPoints: Number(watched.groupsPoints) || 0,
    reflectPoints: Number(watched.reflectPoints) || 0,
    items: (watched.items ?? []).map((i) => ({ ...i, points: Number(i.points) || 0 })) as ReportItem[],
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['teacher-reports'] });
  const save = useMutation({
    mutationFn: (body: ReportTemplateBody) =>
      apiRequest<ReportTemplateDto>(`/api/teacher-reports/templates/${encodeURIComponent(template.id)}`, { method: 'PATCH', body }),
  });
  const publish = useMutation({
    mutationFn: () =>
      apiRequest<ReportTemplateDto>(`/api/teacher-reports/templates/${encodeURIComponent(template.id)}/publish`, { method: 'POST', body: {} }),
  });
  const discard = useMutation({
    mutationFn: () => apiRequest<void>(`/api/teacher-reports/templates/${encodeURIComponent(template.id)}`, { method: 'DELETE' }),
  });

  const build = (): ReportTemplateBody | null => {
    const v = getValues();
    const taken = new Set<string>();
    const body = {
      title: v.title.trim(),
      basePoints: Number(v.basePoints),
      groupsPoints: Number(v.groupsPoints),
      reflectPoints: Number(v.reflectPoints),
      groups: {
        ...v.groups,
        topMin: Number(v.groups.topMin),
        mediumMin: Number(v.groups.mediumMin),
      },
      voiceNote: !!v.voiceNote,
      fields: v.fields.map((f, idx) => {
        const original = template.fields[idx]!;
        const out: ReportField = { key: original.key, label: f.label.trim(), type: original.type };
        if (f.placeholder?.trim()) out.placeholder = f.placeholder.trim();
        if (f.labelMl?.trim()) out.labelMl = f.labelMl.trim();
        if (f.placeholderMl?.trim()) out.placeholderMl = f.placeholderMl.trim();
        if (original.reflect) out.reflect = true;
        return out;
      }),
      items: v.items.map((i) => {
        const key = i.key && originalKeys.has(i.key) ? i.key : slugify(i.label, new Set([...taken, ...originalKeys]));
        taken.add(key);
        const out: ReportItem = { key, label: i.label.trim(), type: i.type, points: Number(i.points) };
        if (i.type === 'counter') out.target = Number(i.target) || 1;
        if (i.type === 'auto') out.verify = i.verify ?? 'attendance_marked';
        if (i.hint?.trim()) out.hint = i.hint.trim();
        if (i.showWhen) out.showWhen = i.showWhen;
        if (i.labelMl?.trim()) out.labelMl = i.labelMl.trim();
        if (i.hintMl?.trim()) out.hintMl = i.hintMl.trim();
        return out;
      }),
    };
    const parsed = reportTemplateBodySchema.safeParse(body);
    if (!parsed.success) {
      toast(templateProblem(parsed.error.issues[0]), { variant: 'error' });
      return null;
    }
    return parsed.data;
  };

  const onSave = handleSubmit(async () => {
    const body = build();
    if (!body) return;
    try {
      const saved = await save.mutateAsync(body);
      reset({ ...saved });
      toast('Draft saved.', { variant: 'success' });
      invalidate();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not save the draft.', { variant: 'error' });
    }
  });

  const onPublish = async () => {
    const body = build();
    if (!body) return;
    const ok = await confirm({
      title: `Publish version ${template.version}?`,
      description:
        'Teachers get this checklist for every report that opens from now on. Reports already submitted keep the version they were filled against.',
      confirmLabel: 'Publish',
    });
    if (!ok) return;
    try {
      await save.mutateAsync(body);
      await publish.mutateAsync();
      toast(`Version ${template.version} is live.`, { variant: 'success' });
      invalidate();
      void navigate({ to: '/system/teacher-reports' });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not publish.', { variant: 'error' });
    }
  };

  const onDiscard = async () => {
    const ok = await confirm({ title: 'Discard this draft?', confirmLabel: 'Discard', variant: 'danger' });
    if (!ok) return;
    try {
      await discard.mutateAsync();
      invalidate();
      void navigate({ to: '/system/teacher-reports' });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not discard.', { variant: 'error' });
    }
  };

  return (
    <form onSubmit={onSave} className="space-y-5 pb-24 lg:pb-0">
      <section className="grid gap-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 sm:grid-cols-4">
        <TextField label="Title" {...register('title')} />
        <TextField label="On-time points" type="number" min={0} {...register('basePoints')} />
        <TextField label="Student-group points" type="number" min={0} {...register('groupsPoints')} />
        <TextField label="Reflection points" type="number" min={0} {...register('reflectPoints')} />
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" className="accent-gold-700" {...register('groups.withPercent')} /> Show each student’s % in the
          student groups
        </label>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" className="accent-gold-700" {...register('groups.askProblemAndSubject')} /> Ask for each group’s
          main problem and toughest subject
        </label>
        <label className="flex items-center gap-2 text-sm sm:col-span-4">
          <input type="checkbox" className="accent-gold-700" {...register('voiceNote')} /> Offer an optional voice note at the end of
          the report (no points)
        </label>
        <TextField label="Top group from (%)" type="number" min={1} max={100} {...register('groups.topMin')} />
        <TextField label="Medium group from (%)" type="number" min={0} max={99} {...register('groups.mediumMin')} />
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Checklist items</h3>
          <span className="text-sm">
            Up to <b className="text-gold-700 dark:text-gold-300">{maxPoints}</b> points
          </span>
        </div>
        <ol className="space-y-2">
          {items.fields.map((field, index) => (
            <ItemRow
              key={field.id}
              index={index}
              count={items.fields.length}
              control={control}
              register={register}
              locked={originalKeys.has(field.key)}
              onMove={(to) => items.move(index, to)}
              onRemove={() => items.remove(index)}
            />
          ))}
        </ol>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          icon={<Plus size={14} aria-hidden="true" />}
          disabled={items.fields.length >= 30}
          onClick={() => items.append({ key: '', label: '', type: 'check', points: 1 })}
        >
          Add item
        </Button>
      </section>

      {template.fields.length > 0 ? (
        <section className="space-y-2">
          <div>
            <h3 className="text-sm font-semibold">Written questions</h3>
            <p className="text-xs text-[color:var(--color-text-muted)]">
              Change the wording or add Malayalam. Questions can’t be added or removed, so past reports still match.
            </p>
          </div>
          <ol className="space-y-2">
            {template.fields.map((f, index) => (
              <li key={f.key} className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <TextField label={`Question ${index + 1}`} {...register(`fields.${index}.label`)} />
                  <TextField label="In Malayalam" optional lang="ml" {...register(`fields.${index}.labelMl`)} />
                  {f.type === 'text' ? (
                    <>
                      <TextField label="Example answer (shown in the empty box)" optional {...register(`fields.${index}.placeholder`)} />
                      <TextField label="Example answer in Malayalam" optional lang="ml" {...register(`fields.${index}.placeholderMl`)} />
                    </>
                  ) : null}
                </div>
                <p className="mt-2 text-[11px] text-[color:var(--color-text-subtle)]">
                  {f.type === 'students' ? 'Teachers pick students' : 'Teachers write an answer'}
                  {f.reflect ? ' · counts toward the reflection points' : ''}
                </p>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <div className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-end gap-2 border-t border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 py-3 sm:-mx-6 sm:px-6">
        <Button type="button" variant="ghost" className="mr-auto text-danger" onClick={() => void onDiscard()} loading={discard.isPending}>
          Discard draft
        </Button>
        <Button type="submit" variant="secondary" loading={save.isPending && !publish.isPending} disabled={!formState.isDirty}>
          Save draft
        </Button>
        <Button type="button" onClick={() => void onPublish()} loading={publish.isPending}>
          Publish version {template.version}
        </Button>
      </div>
    </form>
  );
}

function ItemRow({
  index,
  count,
  control,
  register,
  locked,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  control: Control<FormValues>;
  register: UseFormRegister<FormValues>;
  locked: boolean;
  onMove: (to: number) => void;
  onRemove: () => void;
}) {
  const type = useWatch({ control, name: `items.${index}.type` });
  return (
    <li className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-3">
      <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_90px]">
        <TextField label={`Item ${index + 1}`} placeholder="e.g. Homework checked" {...register(`items.${index}.label`)} />
        <SelectField
          label="How it earns points"
          options={(Object.keys(TYPE_LABEL) as ReportItem['type'][]).map((v) => ({ value: v, label: TYPE_LABEL[v] }))}
          {...register(`items.${index}.type`)}
        />
        <TextField label="Points" type="number" min={0} max={20} {...register(`items.${index}.points`)} />
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {type === 'counter' ? (
          <TextField label="Target" type="number" min={1} {...register(`items.${index}.target`)} />
        ) : null}
        {type === 'auto' ? (
          <SelectField
            label="Portal checks"
            options={(Object.keys(VERIFY_LABEL) as Array<NonNullable<ReportItem['verify']>>).map((v) => ({ value: v, label: VERIFY_LABEL[v] }))}
            {...register(`items.${index}.verify`)}
          />
        ) : null}
        <SelectField
          label="Show"
          options={[
            { value: '', label: 'Always' },
            ...(Object.keys(SHOW_WHEN_LABEL) as Array<NonNullable<ReportItem['showWhen']>>).map((v) => ({ value: v, label: SHOW_WHEN_LABEL[v] })),
          ]}
          {...register(`items.${index}.showWhen`, { setValueAs: (v: string) => (v ? v : undefined) })}
        />
        <TextField label="Hint" optional placeholder="Shown under the item" {...register(`items.${index}.hint`)} />
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <TextField label="Item in Malayalam" optional lang="ml" {...register(`items.${index}.labelMl`)} />
        <TextField label="Hint in Malayalam" optional lang="ml" {...register(`items.${index}.hintMl`)} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="text-[11px] text-[color:var(--color-text-subtle)]">
          {locked ? 'Saved item — past reports stay linked to it' : 'New item — added when you save'}
        </p>
        <div className="flex gap-1">
          <Button type="button" size="sm" variant="ghost" aria-label="Move up" disabled={index === 0} onClick={() => onMove(index - 1)}>
            <ArrowUp size={14} aria-hidden="true" />
          </Button>
          <Button type="button" size="sm" variant="ghost" aria-label="Move down" disabled={index === count - 1} onClick={() => onMove(index + 1)}>
            <ArrowDown size={14} aria-hidden="true" />
          </Button>
          <Button type="button" size="sm" variant="ghost" aria-label={`Remove item ${index + 1}`} disabled={count <= 1} onClick={onRemove}>
            <Trash2 size={14} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </li>
  );
}
